/**
 * ============================================================
 * KEJJA OS — src/lib/pantryApi.ts
 * ============================================================
 */

import { api } from '@/lib/api';

export interface PantryItem {
  id: string;
  homeId: string;
  createdByUserId: string | null;
  name: string;
  emoji: string | null;
  category: string;
  location: string;
  quantity: number;
  unit: string;
  minStockLevel: number | null;
  purchasedOn: string | null;
  expiresOn: string | null;
  notes: string | null;
  isActive: boolean;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isExpiringSoon: boolean;
  daysUntilExpiry: number | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface PantryPulse {
  totalItems: number;
  lowStockCount: number;
  outOfStockCount: number;
  expiringSoonCount: number;
  expiredCount: number;
  byCategory: Array<{ category: string; count: number }>;
}

export interface ItemCreatePayload {
  name: string;
  emoji?: string | null;
  category?: string;
  location?: string;
  quantity?: number;
  unit?: string;
  minStockLevel?: number | null;
  purchasedOn?: string | null;
  expiresOn?: string | null;
  notes?: string | null;
}
export type ItemUpdatePayload = Partial<ItemCreatePayload> & { isActive?: boolean };

export interface ListFilter {
  category?: string;
  location?: string;
  search?: string;
}

const base = (hid: string) => `/homes/${hid}/pantry`;

export const pantryApi = {
  pulse: (hid: string) => api.get<PantryPulse>(`${base(hid)}/pulse`),

  listItems: (hid: string, filter: ListFilter = {}) => {
    const qs = new URLSearchParams();
    if (filter.category) qs.set('category', filter.category);
    if (filter.location) qs.set('location', filter.location);
    if (filter.search) qs.set('search', filter.search);
    const q = qs.toString();
    return api.get<{ items: PantryItem[] }>(`${base(hid)}/items${q ? '?' + q : ''}`);
  },
  listLowStock: (hid: string) => api.get<{ items: PantryItem[] }>(`${base(hid)}/items/low-stock`),
  listExpiring: (hid: string, days = 7) => api.get<{ items: PantryItem[] }>(`${base(hid)}/items/expiring?days=${days}`),

  createItem: (hid: string, body: ItemCreatePayload) =>
    api.post<{ item: PantryItem }>(`${base(hid)}/items`, body),
  updateItem: (hid: string, itemId: string, body: ItemUpdatePayload) =>
    api.patch<{ item: PantryItem }>(`${base(hid)}/items/${itemId}`, body),
  adjustQuantity: (hid: string, itemId: string, delta: number) =>
    api.post<{ item: PantryItem }>(`${base(hid)}/items/${itemId}/adjust`, { delta }),
  deleteItem: (hid: string, itemId: string) =>
    api.delete<void>(`${base(hid)}/items/${itemId}`),

  addFromShopping: (hid: string, body: { name: string; quantity?: number; unit?: string; category?: string }) =>
    api.post<{ item: PantryItem }>(`${base(hid)}/items/from-shopping`, body),
};
